import { useCallback, useEffect, useState } from "react";
import { toast } from "~/components/ui/toaster";
import {
  cancelInvite,
  type Collaborator,
  getCollaborators,
  getPendingInvites,
  inviteCollaborator,
  type PendingInvite,
  removeCollaborator,
} from "../lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";

interface CollaboratorsDialogProps {
  mapId: string;
  canManageCollaborators: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function CollaboratorsDialog({
  mapId,
  canManageCollaborators,
  open,
  onOpenChange,
}: CollaboratorsDialogProps) {
  const [email, setEmail] = useState("");
  const [isInviting, setIsInviting] = useState(false);
  const [collaborators, setCollaborators] = useState<Collaborator[]>([]);
  const [pendingInvites, setPendingInvites] = useState<PendingInvite[]>([]);
  const [loading, setLoading] = useState(true);

  // Load collaborators and invites when dialog opens
  useEffect(() => {
    if (!open) return;

    async function load() {
      setLoading(true);
      try {
        const [collabs, invites] = await Promise.all([
          getCollaborators(mapId),
          canManageCollaborators ? getPendingInvites(mapId) : Promise.resolve([]),
        ]);
        setCollaborators(collabs);
        setPendingInvites(invites);
      } catch {
        toast.error("Failed to load collaborators");
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [mapId, open, canManageCollaborators]);

  const handleInvite = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!email.trim()) return;

      setIsInviting(true);
      try {
        const invite = await inviteCollaborator(mapId, email.trim());
        setPendingInvites((prev) => [...prev, invite]);
        setEmail("");
        toast.success(`Invite sent to ${email.trim()}`);
      } catch (err) {
        const message =
          err instanceof Error && err.message.includes("already")
            ? "This person is already a collaborator"
            : "Failed to send invite";
        toast.error(message);
      } finally {
        setIsInviting(false);
      }
    },
    [mapId, email],
  );

  const handleCancelInvite = useCallback(
    async (inviteId: string) => {
      try {
        await cancelInvite(mapId, inviteId);
        setPendingInvites((prev) => prev.filter((i) => i.id !== inviteId));
        toast.success("Invite cancelled");
      } catch {
        toast.error("Failed to cancel invite");
      }
    },
    [mapId],
  );

  const handleRemoveCollaborator = useCallback(
    async (collaboratorId: string) => {
      try {
        await removeCollaborator(mapId, collaboratorId);
        setCollaborators((prev) => prev.filter((c) => c.id !== collaboratorId));
        toast.success("Collaborator removed");
      } catch {
        toast.error("Failed to remove collaborator");
      }
    },
    [mapId],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="md:max-w-md">
        <DialogHeader>
          <DialogTitle>Collaborators</DialogTitle>
          <DialogDescription>
            {canManageCollaborators ? "Invite people to edit this map with you." : "People who can edit this map."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-2">
          {/* Invite form (can manage only) */}
          {canManageCollaborators && (
            <form onSubmit={handleInvite} className="flex gap-2">
              <Input
                type="email"
                placeholder="Email address"
                aria-label="Collaborator email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1"
              />
              <Button type="submit" disabled={!email.trim() || isInviting}>
                {isInviting ? "Sending..." : "Invite"}
              </Button>
            </form>
          )}

          {loading ? (
            <div className="space-y-2" role="status" aria-label="Loading collaborators">
              <div className="h-10 bg-muted/50 rounded-lg animate-pulse" />
              <div className="h-10 bg-muted/50 rounded-lg animate-pulse" />
            </div>
          ) : (
            <>
              {/* Pending invites (can manage only) */}
              {canManageCollaborators && pendingInvites.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-muted-foreground">Pending Invites</h4>
                  <ul className="space-y-2">
                    {pendingInvites.map((invite) => (
                      <li
                        key={invite.id}
                        className="flex items-center justify-between text-sm bg-muted/50 px-3 py-2 rounded-lg"
                      >
                        <span className="truncate">{invite.email}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCancelInvite(invite.id)}
                          title={`Cancel invite for ${invite.email}`}
                          aria-label={`Cancel invite for ${invite.email}`}
                        >
                          Cancel
                        </Button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Current collaborators */}
              {collaborators.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-muted-foreground">Collaborators</h4>
                  <ul className="space-y-2">
                    {collaborators.map((collab) => (
                      <li
                        key={collab.id}
                        className="flex items-center justify-between text-sm bg-muted/50 px-3 py-2 rounded-lg"
                      >
                        <span className="truncate">{collab.email}</span>
                        {canManageCollaborators && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveCollaborator(collab.id)}
                            title={`Remove ${collab.email}`}
                            aria-label={`Remove ${collab.email}`}
                          >
                            Remove
                          </Button>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Empty state */}
              {collaborators.length === 0 && pendingInvites.length === 0 && (
                <div className="text-sm text-muted-foreground text-center py-4">
                  {canManageCollaborators
                    ? "No collaborators yet. Invite someone to get started."
                    : "No other collaborators on this map."}
                </div>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
